<template>
  <q-tabs
    v-if="optionsProcessed.length !== 0"
    v-bind="$attrs"
    :value="value"
    :name="name"
    class="ws-btn-toggle"
    indicator-color="ws-white"
    v-on="$listeners"
    @input="onUpdate"
  >
    <q-tab
      v-for="(option, i) in optionsProcessed"
      :key="option.value"
      :ripple="false"
      :data-test="`toggle-${option.value}`"
      :name="option.value"
      :label="option.label"
      :style="`width:${100 / optionsProcessed.length}%`"
      :class="{'with-badge': option.badgeValue !== undefined}"
      :disable="getDisabled(option, i)"
    >
      <q-badge v-if="option.badgeValue !== undefined" :class="option.badgeType.toLowerCase()">
        {{ option.badgeValue }}
      </q-badge>
      <ws-tooltip v-if="getDisabled(option, i) && get(option, 'disabledTooltip')">
        {{ option.disabledTooltip }}
      </ws-tooltip>
      <ws-tooltip v-else-if="get(option, 'tooltip')">
        {{ option.tooltip }}
      </ws-tooltip>
      <ws-icon v-if="option.icon" :name="option.icon"></ws-icon>
    </q-tab>
    <input :name="name" type="hidden" :value="value" />
  </q-tabs>
</template>

<script lang="ts">
import {QTabs, QTab} from 'quasar';
import {
  type PropType,
  defineComponent,
  getCurrentInstance,
  computed,
} from 'vue';
import {Theme} from '@odin/services';
import {get} from 'lodash-es';
import {WsIcon} from '@odin/components-vue2';
import { useI18n } from '@odin/services';

export default defineComponent({
  name: 'WsBtnToggle',
  components:{
    QTabs,
    QTab,
    WsIcon
  },
  props: {
    options: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    hiddenOptions: {
      type: Array as PropType<Array<boolean>>,
      default: () => [],
    },
    disabledOptions: {
      type: Array as PropType<Array<boolean>>,
      default: () => [],
    },
    value: {
      type: [String, Number],
      default: '',
    },
    name: {
      type: String,
      default: '',
    }
  },
  emits: ['input'],
  setup(props, vm) {
    const instance = getCurrentInstance() as any;
    const i18next = useI18n();
    const optionsProcessed = computed(() => {
      return props.options.map((option: { label: string; }) => {
        const opt = {...option};
        if (i18next.i18next.exists(option.label)) {
          opt.label = i18next.t(option.label);
        }
        return opt;
      }).filter((_, index: number) => {
        return !get(props.hiddenOptions, index, false);
      });
    });

    const onUpdate = ((value: any): void => {
      vm.emit('input', value);
      if (instance?.proxy.$parent) {
        instance.proxy.$parent.$emit('dirty', value);
      }
    });

    const getDisabled = ((option: {disabled: boolean}, index: number): boolean => {
      return get(props.disabledOptions, index, get(option, 'disabled', false));
    });

    return {onUpdate, optionsProcessed, Theme, get, getDisabled};
  }
});
</script>

<style lang="scss" scoped>
.ws-btn-toggle {
  border: none;
  padding: map-get($space-xs, x);
  color: $toggle-button-color;
  background: $toggle-button-bg;
  box-shadow: none;
  border-radius: $button-border-radius;
  position: relative;
  /* z-index to compensate the tab indicator's stacking order change
  when Quasar applies transition and/or transformation */
  z-index: 0;

  &[disabled] ::v-deep .q-tab {
    pointer-events: none;
  }

  .theme--loopia & {
    background: var(--toggle-button-bg);
  }

  ::v-deep {
    .q-tab {
      min-height: map-get($space-xl, x);
      padding: 0 map-get($space-sm, x);
      margin: 0;
      border-radius: $button-border-radius;
      text-transform: none;
      align-content: center;
      color: $toggle-button-active-color;

      .theme--loopia & {
        color: $ws-base160;
      }
    }

    .q-tab.with-badge .q-tab__content {
      flex-direction: row;
    }

    .q-focus-helper {
      display: none;
    }

    .q-badge {
      margin-left: map-get($space-sm, x);
      border-radius: 3px;

      // TODO: replace with variables after Figma tokens are implemented
      &.success {
        background: $ws-gem-100;
      }

      &.empty {
        background: $ws-content-disabled;
      }

      &.warning {
        background: $ws-sun-110;
      }

      &.danger {
        background: $ws-brick-200;
      }

      &.base {
        background: $ws-king-200;
      }

      &.positive {
        background: $ws-gem-140;
      }

      &.neutral {
        background: $ws-navy-80;
      }

      &.alert {
        background: $ws-pumpkin-200;
      }

      &.dark {
        background: $ws-navy-100;
      }

      &.premium {
        background: $ws-plum-100;
      }

      &.basic {
        background: $ws-aqua-120;
      }

      &.advanced {
        background: $ws-pumpkin-100;
      }
    }
  }
}

.wsk-root .ws-btn-toggle::v-deep, .ws-btn-toggle::v-deep {
  .q-tab--active .q-tab__indicator {
    height: 100%;
    border-radius: $button-border-radius;
    background-color: $toggle-button-active-tab-bg;
    z-index: -1;

    .theme--loopia & {
      background-color: var(--toggle-button-active-tab-bg);
    }
  }

  .q-tab .q-tab__label,
  .q-tab--active .q-tab__label {
    @include ws-typo-small;
    font-weight: normal;
    text-overflow: ellipsis;
    transition: font-weight 150ms ease-in-out,
    font-size 150ms ease-in-out;

    overflow: hidden;
    max-width: calc(
      100% + #{map-get($space-sm, x)}
    ); // sm space for right padding

    .theme--loopia & {
      font-weight: 700;
    }
  }

  .q-tab--active .q-tab__label, .q-tab:not(.disabled):hover .q-tab__label {
    font-weight: 700;
  }

  .q-tab:not(.q-tab--active):hover .q-tab__label {
    .theme--loopia & {
      font-size: 1.05em;
    }
  }

  .q-tab--active {
    .theme--loopia & {
      color: var(--toggle-button-active-color);
    }
  }
}
</style>
